/**
 * Check block assembler — transforms one canonical CheckResult (§A1) into a
 * structured block spec that templates can render. The full per-check activity
 * list is surfaced separately as the embedded XLSX attachment.
 *
 * Render-only contract per §A2: this assembler may format engine values for
 * display (e.g. "61 of 61 (100.0%)" → strings), but MUST NOT recompute
 * thresholds, severities, or pass/fail.
 *
 * §A3 routing: finding_type drives the variant. activity_level / schedule_level
 * / report_only have separate body templates and chip wording. Only
 * activity_level uses "PASS — No issues found"; schedule_level EXCEEDED gets
 * "STRUCTURAL FINDING".
 *
 * §A4: chip color follows status_threshold for activity_level (threshold-only
 * red/green), and status_severity for schedule_level STRUCTURAL FINDING
 * (red/amber per severity tier — the only place severity touches the per-check
 * chip, explicitly approved in 10D-1.5/Sub-2 greenlight Q3).
 *
 * @module pdf/helpers/check-block-assembler
 */

import {
  buildTableData,
  NO_TABLE_CHECKS,
} from './activity-table-column-schema.js';
import { buildHistogramFromEngineBuckets } from './float-histogram-builder.js';
import { COLORS, severityColor } from '../designTokens.js';
import { STATUS_THRESHOLD, STATUS_SEVERITY, FINDING_TYPE } from '../../checks/checkResult.js';
import {
  sortFindingsByFloatPosition,
  getFloatPositionPills,
  formatLoadingComposition,
  buildResourceLoadingAction,
} from '../../shared/check36Display.js';
import { xlsxFilenameFor, buildXlsxPointer } from '../../shared/checkExport.js';
import { ACTIVITY_PREVIEW_ROWS, smartPreviewCap } from '../../shared/activityPreview.js';
import { emptyStateFor } from '../../shared/emptyStates.js';
import {
  buildReportOnlyLine,
  statusLabelFor,
  REPORT_ONLY_CHIP_LABEL,
  NO_TABLE_BODY_TEXT,
} from '../../shared/checkDisplay.js';
import {
  BASELINE_COMPARISON_CHECKS,
  buildComparisonResultPhrase,
  buildComparisonBody,
  simplifyWbsPath,
} from '../../shared/baselineComparisonDisplay.js';

/** Max rows rendered in the PDF before we truncate; the full list lives in the attached XLSX. */
export const DEFAULT_MAX_ROWS = 40;

/** Per-check preview: top-N impact-sorted (§6) sample shared with the dashboard;
 *  full list lives in the attached XLSX. */
export const V6_TABLE_ROWS = ACTIVITY_PREVIEW_ROWS;

/**
 * Smart-show rule (10D-1.5/Sub-2 fix-up X5): when truncation would hide
 * exactly one row (totalCount === V6_TABLE_ROWS + 1), show all rows
 * instead. A "Showing 4 of 5" badge is worse UX than just rendering the
 * fifth row. MEGAC-FIX F3: the rule itself now lives in
 * shared/activityPreview.js (smartPreviewCap) so the comparison tables share
 * the identical contract.
 */
function smartCap(total) {
  return smartPreviewCap(total, V6_TABLE_ROWS);
}

// simplifyWbsPath now lives in the shared comparison display module (single
// source for the leaf-WBS logic, consumed by both the PDF assembler and the
// dashboard); re-exported here to preserve the existing import path.
export { simplifyWbsPath };

/**
 * Checks whose findings describe WBS / structural items rather than activities.
 * They render as a 2-column Item / Detail table — the 5-column activity table
 * produces meaningless em-dashes in WBS / FLOAT / DURATION for these.
 */
export const STRUCTURAL_CHECKS = new Set([29, 30, 37]);

/** The render variants carrying a Baseline Alignment comparison body. */
export const COMPARISON_VARIANTS = new Set(['comparisonTable', 'comparisonLine', 'comparisonStatement']);

/**
 * Resolve the variant for a check block. Routes on canonical finding_type
 * per §A3.
 */
export function variantFor(checkResult) {
  const cn = checkResult.check_number;
  const ft = checkResult.finding_type;

  if (ft === FINDING_TYPE.REPORT_ONLY) {
    // BASELINE-SUB4: the two-file comparison checks (all REPORT_ONLY) route to
    // real comparison bodies instead of the count-only report-only chip. The
    // engine's finding_type is untouched — this is a render-side routing choice.
    if (BASELINE_COMPARISON_CHECKS.has(cn)) {
      if (cn === 34) return 'comparisonLine';
      if (cn === 35) return 'comparisonStatement';
      return 'comparisonTable'; // 31, 32, 33
    }
    if (cn === 19) return 'histogram';
    // CERT-FIX-1B: #21 goes report-only below 100 analyzable activities, but it
    // is a SCHEDULE-LEVEL proportion (not a finding count) — route it to the
    // summary-line variant so the proportion stays visible ("emitted for
    // display"), with a REPORT ONLY chip from buildV6ResultDisplay, instead of
    // the count-only report-only block that would read "0 items reported".
    if (cn === 21) return 'noTable';
    return 'reportOnly';
  }

  if (ft === FINDING_TYPE.SCHEDULE_LEVEL) {
    if (STRUCTURAL_CHECKS.has(cn)) return 'structural';
    return 'noTable';
  }

  // activity_level
  const findingCount = (checkResult.severity_findings || []).length;
  if (findingCount === 0) {
    // G-2 (Batch-RENDER-36): an activity-level check that reads EXCEEDED with
    // ZERO findings is the degenerate not-assessable population (e.g. #36
    // with no resource-analysable activities). Its empty body must not
    // borrow the PASS shape.
    return checkResult.status_threshold === STATUS_THRESHOLD.EXCEEDED
      ? 'emptyExceeded'
      : 'emptyPass';
  }
  if (NO_TABLE_CHECKS.has(cn)) return 'noTable';
  return 'table';
}

/**
 * Compute the result/threshold display object. Reads canonical fields verbatim:
 *   resultText  ← numerator/denominator/ratio_pct
 *   thresholdText ← threshold_label (engine-locked, §3 table)
 *   chipLabel/chipColor ← status_threshold + status_severity per §A3/§A4
 *
 * Returns null for variants that skip the result line (report_only, histogram).
 */
export function buildV6ResultDisplay(checkResult) {
  const ft = checkResult.finding_type;
  const cn = checkResult.check_number;

  // BASELINE-SUB4: comparison checks surface a canonical result phrase and a
  // neutral REPORT ONLY chip. There is no threshold line — REPORT_ONLY carries
  // no pass/fail, and Category 7 must show no severity badge (§A4/§S1).
  if (BASELINE_COMPARISON_CHECKS.has(cn)) {
    return {
      resultText: buildComparisonResultPhrase(checkResult),
      thresholdText: null,
      exceeded: false,
      chipLabel: statusLabelFor(checkResult.status_threshold, ft),
      chipColor: COLORS.gray400,
    };
  }

  // CERT-FIX-1B: report-only #21 (small-schedule guard) still shows its
  // proportion — the same "N of D denomLabel (P%)" line the scored state uses —
  // with the informational threshold label and a neutral REPORT ONLY chip.
  if (ft === FINDING_TYPE.REPORT_ONLY && cn === 21) {
    const flagged = checkResult.numerator ?? 0;
    const total = checkResult.denominator ?? 0;
    const pct = checkResult.ratio_pct;
    const denomLabel = checkResult.denominator_label;
    const tail = denomLabel ? ` ${denomLabel}` : '';
    return {
      resultText: `${flagged} of ${total}${tail} (${(pct ?? 0).toFixed(1)}%)`,
      thresholdText: checkResult.threshold_label,
      exceeded: false,
      chipLabel: statusLabelFor(checkResult.status_threshold, ft),
      chipColor: COLORS.gray400,
    };
  }

  if (ft === FINDING_TYPE.REPORT_ONLY) return null;

  const flagged = checkResult.numerator ?? 0;
  const total = checkResult.denominator ?? 0;
  const pct = checkResult.ratio_pct;
  const denomLabel = checkResult.denominator_label;
  const override = checkResult.result_display_override;
  const exceeded = checkResult.status_threshold === STATUS_THRESHOLD.EXCEEDED;

  // PDF-POLISH-2 D22: binary checks (e.g. #20 CP Continuity) replace the
  // misleading "0 of 1" rendering entirely. Engine emits {pass, fail} strings.
  let resultText;
  if (override && typeof override === 'object') {
    resultText = exceeded ? override.fail : override.pass;
  } else if (typeof pct === 'number' && total > 0) {
    const tail = denomLabel ? ` ${denomLabel}` : '';
    // v3.17 §1: the MEGA-C #21 "of scheduled work" basis suffix is retired —
    // #21 is now a pure activity count/count, so ratio_pct derives EXACTLY
    // from the displayed pair and the two-projections caveat no longer exists.
    resultText = `${flagged} of ${total}${tail} (${pct.toFixed(1)}%)`;
  } else if (total > 0) {
    const tail = denomLabel ? ` ${denomLabel}` : '';
    resultText = `${flagged} of ${total}${tail}`;
  } else {
    resultText = `${flagged} ${flagged === 1 ? 'activity' : 'activities'}`;
  }

  // Chip label from THE shared §A3 mapping (STAGE4-FIX-B Part 2 — the
  // dashboard chip consumes the same function); color per §A4 / Q3 greenlight.
  const chipLabel = statusLabelFor(checkResult.status_threshold, ft);
  let chipColor;
  if (checkResult.status_threshold === STATUS_THRESHOLD.REPORT_ONLY) {
    // F1 (D-Check37ReportOnlyChip, v3.36): a schedule-level check forced to
    // REPORT_ONLY status (unused-calendar-only #37) chips the neutral
    // report-only grey — the same colour the reportOnly/comparison branches
    // use above — never the green PASS colour. The dashboard chip is CSS-keyed
    // on the label string, so its .check-chip-report-only class already renders
    // neutral once statusLabelFor returns REPORT ONLY.
    chipColor = COLORS.gray400;
  } else if (ft === FINDING_TYPE.SCHEDULE_LEVEL && exceeded) {
    chipColor = severityColor(checkResult.status_severity || STATUS_SEVERITY.WARNING);
  } else if (exceeded) {
    chipColor = COLORS.warning;
  } else {
    chipColor = COLORS.pass;
  }

  return {
    resultText,
    thresholdText: checkResult.threshold_label || '\u2014',
    exceeded,
    chipLabel,
    chipColor,
  };
}

/**
 * Assemble a single check block spec — template-ready data for one check.
 *
 * Corrective action is a passthrough: analyzeSchedule attaches
 * `correctiveAction` directly to every check before reaching us, so we read
 * it verbatim and only gate it on EXCEEDED per §A2.
 *
 * @param {object} checkResult — canonical CheckResult §A1 (with
 *   .correctiveAction attached by analyzeSchedule)
 * @param {object} [opts]
 * @param {number} [opts.maxRows=40]
 */
export function assembleCheckBlock(checkResult, opts = {}) {
  const maxRows = opts.maxRows || DEFAULT_MAX_ROWS;
  const cn = checkResult.check_number;
  const variant = variantFor(checkResult);
  const isComparisonVariant = COMPARISON_VARIANTS.has(variant);

  const rawFindings = checkResult.severity_findings || [];
  // 10D-2/Sub-2: Check #36 surfaces findings sorted critical → near_critical →
  // floating (then taskCode ascending within band). Other checks keep
  // engine-emission order.
  const findings = cn === 36
    ? sortFindingsByFloatPosition(rawFindings)
    : rawFindings;
  const findingCount = findings.length;

  const exceeded = checkResult.status_threshold === STATUS_THRESHOLD.EXCEEDED;
  const hasActivityTable = variant === 'table' || variant === 'noTable';
  const emitExport = findingCount > 0;

  let table = null;
  if (hasActivityTable) {
    const { columns, rows } = buildTableData(cn, findings);
    const truncated = rows.length > maxRows;
    table = {
      columns,
      rows: truncated ? rows.slice(0, maxRows) : rows,
      totalCount: rows.length,
      truncated,
    };
  }

  // 2-column Item / Detail table for WBS / structural findings (#29, #30, #37).
  let v6Structural = null;
  if (variant === 'structural') {
    const allRows = findings.map((f) => ({
      item: f.taskName ?? '',
      detail: f.taskCode ?? '',
    }));
    const { cap: structCap, truncated: structTrunc } = smartCap(allRows.length);
    v6Structural = {
      rows: allRows.slice(0, structCap),
      totalCount: allRows.length,
      truncated: structTrunc,
    };
  }

  let v6Table = null;
  if (variant === 'table') {
    const v6Cols = [
      // F8 (B-PdfIdWrap, v3.36): the PDF per-check table renders THIS hardcoded
      // 5-column set, bypassing getColumnsForCheck — so the schema's
      // activityId `nowrap` (X-3/V-22) never reached the ID cell here, and a
      // long hyphenated ID wrapped mid-token under table-layout:auto width
      // pressure (visible on the wider #8/#10/#11/#12/#28 tables). Carry the
      // nowrap hint on the ID column directly; _activity-table.ejs honours it.
      { key: 'taskCode',       label: 'ID',       align: 'left', nowrap: true },
      { key: 'taskName',       label: 'ACTIVITY', align: 'left' },
      { key: 'wbsLeaf',        label: 'WBS',      align: 'left' },
      { key: 'totalFloatDays', label: 'FLOAT',    align: 'right', kind: 'float' },
      { key: 'durationDays',   label: 'DURATION', align: 'right' },
    ];
    const { cap: tblCap, truncated: tblTrunc } = smartCap(findings.length);
    const sampleRows = findings.slice(0, tblCap).map((f) => ({
      taskCode: f.taskCode ?? '',
      taskName: f.taskName ?? '',
      wbsLeaf: simplifyWbsPath(f.wbsPath),
      totalFloatDays: (f.totalFloatDays === null || f.totalFloatDays === undefined)
        ? null
        : Number(f.totalFloatDays),
      durationDays: (f.durationDays === null || f.durationDays === undefined)
        ? null
        : Number(f.durationDays),
      // 10D-2/Sub-2: per-row float-position tag for Check #36 left-border
      // treatment. Other checks emit undefined here and the table renders
      // without the band border (backward-compatible).
      floatPosition: cn === 36 ? (f.float_position ?? null) : undefined,
    }));
    v6Table = {
      columns: v6Cols,
      rows: sampleRows,
      totalCount: findings.length,
      truncated: tblTrunc,
    };
  }

  const v6Result = buildV6ResultDisplay(checkResult);

  // BASELINE-SUB4: normalised comparison body (activity table / relationship
  // line / statement) for #31–35. Built from the shared display module so the
  // PDF and dashboard render identical structures.
  const comparison = isComparisonVariant ? buildComparisonBody(checkResult) : null;

  let histogram = null;
  if (variant === 'histogram') {
    // SURFACE-COHERE (R14): render the engine's EMITTED #19 buckets (the
    // canonical 7-bin + noData set) and its distribution_summary verbatim —
    // no re-binning of raw float-days, no second composer. The noData bar is
    // dropped at 0 inside the helper (both surfaces). Legacy reports whose
    // dashboard.json predates this era carry the old 6 buckets and no summary;
    // the helper renders whatever buckets they carry and the summary is ''.
    histogram = buildHistogramFromEngineBuckets(
      checkResult.buckets,
      checkResult.distribution_summary,
    );
  }

  // Corrective action only renders for EXCEEDED checks per §A2 (PASS = no
  // problem, no action). Report-only checks have their own info chip path.
  // analyzeSchedule already attached check.correctiveAction; this is a
  // verbatim passthrough — never recomputed here.
  let correctiveAction = (exceeded && checkResult.correctiveAction) ? checkResult.correctiveAction : null;

  // 10D-2/Sub-2: Check #36 detail-page extras. The engine emits the
  // float_position_breakdown and loading_type_breakdown; the display layer
  // surfaces them as a pill cluster + composition line. The corrective
  // statement is overridden by an adaptive K/M/L-aware sentence — engine
  // text is generic and lacks critical-path priority signal.
  //
  // 10D-3/Sub-2: the K>0 priority clause is mode-keyed (Rule G/C/R). Mode
  // travels on the engine-attached check.correctiveAction.mode; reading it
  // here preserves §A2 (display formats, does not recompute) — we are not
  // re-running mode selection, just propagating it to the K/M/L override.
  let floatPositionPills = null;
  let loadingComposition = null;
  if (cn === 36) {
    // MEGAC-FIX F7: getFloatPositionPills returns [] when the check carries
    // zero findings — null it out so the template's chip-row block is skipped
    // entirely (no empty container).
    const pills = getFloatPositionPills(checkResult);
    floatPositionPills = pills.length > 0 ? pills : null;
    loadingComposition = formatLoadingComposition(checkResult);
    if (correctiveAction) {
      const mode = checkResult.correctiveAction?.mode || 'general';
      const adaptive = buildResourceLoadingAction(checkResult, mode);
      if (adaptive) {
        correctiveAction = { ...correctiveAction, statement: adaptive };
      }
    }
  }

  // MEGAC-FIX F3: comparison checks #31–34 now carry an XLSX (their inline
  // tables share the standard preview cap); only #35 stays inline-only. Kept
  // in lock-step with isExportableCheck so the attachment set and the block
  // set stay equal.
  const xlsxFilename = (emitExport && cn !== 35)
    ? xlsxFilenameFor(cn, checkResult.check_name || '')
    : null;
  // MEGAC-FIX F13: the attachment-pointer sentence is built ONCE (shared
  // builder) from the TRUE finding total — templates render it verbatim.
  const totalForLink = (v6Table && v6Table.totalCount)
    || (v6Structural && v6Structural.totalCount)
    || (comparison && comparison.totalCount)
    || findingCount;
  const attachmentNote = xlsxFilename && findingCount > 0
    ? buildXlsxPointer(totalForLink, xlsxFilename)
    : null;

  const block = {
    checkNumber: cn,
    checkName: checkResult.check_name || '',
    variant,
    // STAGE4-FIX (v3.18 §c / §A2): the report-only count line is built ONCE in
    // the shared module — the PDF partial and the dashboard body render the
    // identical string; neither surface re-types it. FIX-B: the chip label
    // travels the same way (the partial renders it verbatim).
    reportOnlyLine: variant === 'reportOnly' ? buildReportOnlyLine(findingCount) : null,
    reportOnlyChipLabel: variant === 'reportOnly' ? REPORT_ONLY_CHIP_LABEL : null,
    // FIX-B Part 1: the summary-only body line is the shared constant — the
    // dashboard renders the identical string in its neutral no-table body.
    noTableBodyText: variant === 'noTable' ? NO_TABLE_BODY_TEXT : null,
    findingType: checkResult.finding_type,
    statusThreshold: checkResult.status_threshold,
    statusSeverity: checkResult.status_severity,
    exceeded,
    findingCount,
    description: checkResult.explanation || '',
    table,
    v6Table,
    v6Structural,
    v6Result,
    histogram,
    correctiveAction,
    xlsxFilename,
    attachmentNote,
    floatPositionPills,
    loadingComposition,
    comparison,
  };

  if (variant === 'emptyPass') {
    block.activityCount = checkResult.denominator ?? null;
    // F6 (B-Check3PassNoun, v3.36): carry the denominator noun so the PASS
    // empty state reads "N relationships analysed" for relationship-denominated
    // checks (#3) instead of the wrong "N activities". Only the activity-level
    // emptyPass path carries it; the structural empty-pass path is unchanged.
    block.denominatorLabel = checkResult.denominator_label ?? null;
  }
  if (variant === 'emptyExceeded') {
    block.activityCount = checkResult.denominator ?? null;
    block.emptyState = emptyStateFor(checkResult); // G-2: canonical not-assessable text
  }

  return block;
}

/**
 * Assemble every block in the analysis, keyed by check number.
 *
 * @param {object} analysis
 * @param {object} [opts]
 * @param {number} [opts.maxRows]
 */
export function assembleAllBlocks(analysis, opts = {}) {
  const maxRows = opts.maxRows || DEFAULT_MAX_ROWS;
  const blocks = [];

  for (const cat of analysis.categories || []) {
    if (cat.hidden) continue;
    for (const check of cat.checks || []) {
      if (check.hidden) continue;
      blocks.push(assembleCheckBlock(check, { maxRows }));
    }
  }

  return { blocks };
}
